import React from 'react';
import TextField from 'ui/elements/form/TextField';
import Select from 'ui/elements/form/Select/Select';
import { SimpleLabel } from 'types/company/company';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import IconButton from 'ui/elements/icons/IconButton';
import { bluePlanetTheme } from 'ui/theme';

interface ColorOption {
  label: string;
  value: string;
}

interface LabelEditor {
  label: SimpleLabel;
  index: number;
  renderLabel: (label: SimpleLabel) => React.ReactNode;
  onUpdate: (index: number, updates: Partial<SimpleLabel>) => void;
  onRemove: (index: number) => void;
}

// Create color options from theme
const createColorOptions = (): ColorOption[] => {
  const palette = bluePlanetTheme.bluePlanetPalette;
  const options: ColorOption[] = [];

  // Add all color variations from the theme
  const colorGroups = [
    { name: 'Blue', colors: palette.blue },
    { name: 'Red', colors: palette.red },
    { name: 'Green', colors: palette.green },
    { name: 'Orange', colors: palette.orange },
    { name: 'Purple', colors: palette.purple },
    { name: 'Violet', colors: palette.violet },
    { name: 'Yellow', colors: palette.yellow },
    { name: 'Lime', colors: palette.lime },
    { name: 'Grey', colors: palette.grey },
    { name: 'Indigo', colors: palette.indigo },
  ];

  colorGroups.forEach(group => {
    Object.entries(group.colors).forEach(([shade, color]) => {
      if (typeof color === 'string') {
        options.push({
          label: `${group.name} ${shade.charAt(0).toUpperCase() + shade.slice(1)}`,
          value: color,
        });
      }
    });
  });

  // Add special colors
  options.push(
    { label: 'White', value: palette.white },
    { label: 'Black', value: palette.black },
    { label: 'Off White', value: palette.offWhite },
  );

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

const renderColorOption = (props: any, renderLabel: (label: SimpleLabel) => React.ReactNode, option: ColorOption) => (
  <li {...props}>{renderLabel({ name: option.label, color: option.value })}</li>
);
export default function LabelEditor({ label, renderLabel, index, onUpdate, onRemove }: LabelEditor) {
  const handleColorSelection = (colorOption: ColorOption | undefined) => {
    if (colorOption) {
      onUpdate(index, { color: colorOption.value });
    }
  };
  const colorOptions = createColorOptions();

  return (
    <div className="u-flex u-flex-align-center u-quarter-spacing-bottom u-flex--gap-1">
      <div style={{ alignItems: 'end' }} className="u-flex-grow u-flex  u-flex--gap-1">
        <TextField
          value={label.name}
          placeholder="Enter label name"
          onChange={e => onUpdate(index, { name: e.target.value })}
        />
        <TextField
          placeholder={bluePlanetTheme.bluePlanetPalette.blue.medium}
          value={label.color}
          onChange={e => onUpdate(index, { color: e.target.value })}
        />
        <div style={{ minWidth: '150px' }}>
          <Select<ColorOption>
            options={colorOptions}
            value={colorOptions.find(option => option.value === label.color)}
            onChange={handleColorSelection}
            getOptionLabel={option => option.label}
            renderOption={(props, option) => renderColorOption(props, renderLabel, option)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        </div>
      </div>
      <div style={{ minWidth: '100px' }}>{renderLabel({ ...label, name: label.name || 'New label' })}</div>
      <IconButton color="grey" size="small" onClick={() => onRemove(index)}>
        <TrashIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
